import React from "react";
import { Alert, Box, Button, Checkbox, Chip, FormControlLabel, FormGroup, IconButton, Paper, TextField, Tooltip, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { PageContainer, SectionHeader, SelectField, FormDialog, DataTable } from "../../Layout/Common";
import { gs, Loader, StaleHint, ensureOptions, ensureRegions, canEdit, StateChip, AutoDrainChip, IdText, FIXED, ELLIPSIS,
         when, fromUnix, n, regionLabel, titleRegionIds, versionLabel, majorMinor, shortId, openMachine, openSession } from "./servers";

// Game Servers → Worlds: the title's persistent worlds (`servers worlds`, polled every 5 s). A world is a GROUP of shards
// (§15): one persistent session per shard, each on its own server, per network region; the base shards
// (minShardsPerRegion per region) never drain, the scaler adds and drains the extra ones. A world of before the shards is
// shard 1 (base) of its region. Add (`world-add` with the regions) / Remove (`world-remove`: every shard ends).

const seats = (s) => `${n(s.seated)} / ${n(s.joined)} / ${n(s.maxPlayers)}`;
const worldName = (w) => w.world.world ?? w.world.name ?? shortId(w.world.id);
const sessionVersion = (s) => versionLabel(s.branch, s.versionText || `${s.major}.${s.minor}`);

// One entry per shard -> [{ name, shards: [...] }] by world name, shards by region then shard number
function groupWorlds(worlds) {
    const map = new Map();
    for (const w of worlds ?? []) {
        const name = worldName(w);
        if (!map.has(name)) map.set(name, []);
        map.get(name).push(w);
    }
    const groups = [...map.entries()].map(([name, shards]) => {
        shards.sort((a, b) => String(a.world.region ?? "").localeCompare(String(b.world.region ?? "")) || (a.world.shard ?? 1) - (b.world.shard ?? 1));
        const regions = new Map();
        for (const s of shards) regions.set(s.world.region ?? "?", (regions.get(s.world.region ?? "?") ?? 0) + 1);
        return { name, shards, regions: [...regions.entries()], first: shards[0].world,
                 players: shards.reduce((a, s) => a + (s.world.joined ?? 0), 0), seated: shards.reduce((a, s) => a + (s.world.seated ?? 0), 0) };
    });
    groups.sort((a, b) => a.name.localeCompare(b.name));
    return groups;
}

export default class WorldsPage extends React.Component {
    state = { worlds: null, options: null, optionsError: null, netRegions: null, regionsError: null, loadedAt: null, stale: null, dialog: null, busy: false };

    loader = new Loader(this, async () => {
        ensureOptions(this);
        ensureRegions(this);
        const r = await gs("Loading worlds", ["worlds"], { silent: true });
        return { worlds: r?.worlds ?? [] };
    });

    componentDidMount() { this.loader.start(); }
    componentWillUnmount() { this.loader.stop(); }

    remove = (g) => {
        const shards = g.shards.length;
        if (!window.confirm(`Remove world '${g.name}'?\n\nEvery shard ends now (${shards} shard${shards === 1 ? "" : "s"}, ${n(g.players)} joined players are disconnected) and their servers stop.`)) return;
        gs("Removing the world", ["world-remove", g.first.id], { retry: false })
            .then(r => { if (r?.worlds) this.setState({ worlds: r.worlds }); this.loader.load(); })
            .catch(() => {});
    };

    // ----- add -----
    persistentProfiles = () => (this.state.options?.profiles ?? []).filter(p => String(p.mode).toLowerCase() === "persistent");
    // The major.minors of a branch that have a server build (newest first)
    lines = (branch) => {
        const seen = new Set(), out = [];
        for (const b of (this.state.options?.builds ?? []).filter(x => x.branch === branch).sort((a, b) => b.version - a.version)) {
            const mm = majorMinor(b.versionText);
            if (mm && !seen.has(mm)) { seen.add(mm); out.push({ value: mm, label: `${mm} (newest patch ${b.versionText})` }); }
        }
        return out;
    };
    openAdd = () => {
        const branches = this.state.options?.branches ?? [];
        const branch = branches.includes("Development") ? "Development" : (branches[0] ?? "");
        this.setState({ dialog: { name: "", profile: this.persistentProfiles()[0]?.id ?? "", branch, line: this.lines(branch)[0]?.value ?? "",
                                  regions: titleRegionIds(this.state.netRegions), maxPlayers: "", data: "" } });
    };
    setDialog = (patch) => this.setState(st => {
        const dialog = { ...st.dialog, ...patch };
        if ("branch" in patch && !this.lines(dialog.branch).some(l => l.value === dialog.line)) dialog.line = this.lines(dialog.branch)[0]?.value ?? "";
        return { dialog };
    });
    toggleRegion = (id) => {
        const r = this.state.dialog.regions;
        this.setDialog({ regions: r.includes(id) ? r.filter(x => x !== id) : [...r, id] });
    };
    dialogError = () => {
        const w = this.state.dialog;
        if (!w.name.trim()) return "A world needs a name.";
        if (!w.profile) return "Pick a persistent profile.";
        if (!w.branch || !/^\d+\.\d+$/.test(w.line)) return "Pick a branch and a major.minor with a server build.";
        if (w.regions.length === 0) return "Pick at least one region.";
        if (String(w.maxPlayers).trim() !== "" && !(parseInt(w.maxPlayers, 10) > 0)) return "Max players must be > 0 (empty = the profile's).";
        if (w.data.trim()) { try { JSON.parse(w.data); } catch (e) { return `Data is not valid JSON: ${e.message}`; } }
        return "";
    };
    add = () => {
        const w = this.state.dialog;
        const [major, minor] = w.line.split(".").map(x => parseInt(x, 10));
        // the region order is the table's (the title's order first)
        const order = titleRegionIds(this.state.netRegions);
        const regions = [...w.regions].sort((a, b) => (order.indexOf(a) + 1 || 99) - (order.indexOf(b) + 1 || 99));
        const payload = { name: w.name.trim(), profile: w.profile, branch: w.branch, major, minor, regions };
        if (String(w.maxPlayers).trim() !== "") payload.maxPlayers = parseInt(w.maxPlayers, 10);
        if (w.data.trim()) payload.data = JSON.parse(w.data);
        this.setState({ busy: true });
        gs("Adding the world", ["world-add"], { payload, retry: false })
            .then(r => { if (r?.worlds) this.setState({ worlds: r.worlds }); this.setState({ dialog: null }); })
            .catch(() => {})
            .finally(() => this.setState({ busy: false }));
    };

    renderDialog() {
        const { dialog: w, options, optionsError, netRegions, regionsError, busy } = this.state;
        if (!w) return null;
        const err = this.dialogError();
        const profiles = this.persistentProfiles();
        const lines = this.lines(w.branch);
        const p = profiles.find(x => x.id === w.profile);
        const regions = (netRegions?.regions ?? []).filter(r => r.enabled !== false || w.regions.includes(r.id));
        const base = Math.max(1, p?.minShardsPerRegion ?? 1);
        return <FormDialog open title="Add a world" onClose={() => this.setState({ dialog: null })} onConfirm={this.add}
                           confirmText="Add world" confirmDisabled={!!err || busy} maxWidth="sm" fullWidth>
            {!options && <Alert severity={optionsError ? "error" : "info"} sx={{ mb: 1 }}>{optionsError ? `Could not load the profiles and builds: ${optionsError}` : "Loading the profiles and builds…"}</Alert>}
            {options && profiles.length === 0 && <Alert severity="warning" sx={{ mb: 1 }}>This title declares no persistent profile (GameServersConfig.profiles).</Alert>}
            <TextField fullWidth label="Name (unique among the live worlds)" sx={{ mt: 1 }} value={w.name} onChange={e => this.setDialog({ name: e.target.value })} />
            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                <SelectField label="Profile (persistent)" fullWidth value={w.profile} onChange={v => this.setDialog({ profile: v })}
                             options={profiles.map(x => ({ value: x.id, label: `${x.id} · ${x.maxPlayers} players · ${x.weight} unit(s)${x.scalable ? " · scalable" : ""}` }))} />
                <SelectField label="Branch" fullWidth value={w.branch} onChange={v => this.setDialog({ branch: v })} options={options?.branches ?? []} />
            </Box>
            <SelectField label="Version (major.minor)" fullWidth sx={{ mt: 2 }} value={w.line} onChange={v => this.setDialog({ line: v })} options={lines} disabled={lines.length === 0} />
            {lines.length === 0 && options && <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>No server build on {w.branch}: upload one on the Database page.</Typography>}
            <Typography variant="subtitle2" sx={{ mt: 2 }}>Regions (a base shard in each)</Typography>
            {regionsError && !netRegions && <Typography variant="caption" color="error" sx={{ display: "block" }}>Could not load the regions: {regionsError}</Typography>}
            <FormGroup row>
                {regions.map(r => <FormControlLabel key={r.id} label={`${r.id} · ${r.name}${r.enabled === false ? " (disabled)" : ""}`}
                    control={<Checkbox checked={w.regions.includes(r.id)} onChange={() => this.toggleRegion(r.id)} />} />)}
            </FormGroup>
            <TextField label="Max players per shard" type="number" sx={{ mt: 2, width: 260 }} value={w.maxPlayers} onChange={e => this.setDialog({ maxPlayers: e.target.value })}
                       helperText={p ? `empty = the profile's ${p.maxPlayers}` : "empty = the profile's"} slotProps={{ htmlInput: { min: 1 } }} />
            <TextField label="Data (the title's JSON, optional)" fullWidth multiline minRows={3} maxRows={10} sx={{ mt: 2 }} value={w.data}
                       onChange={e => this.setDialog({ data: e.target.value })} slotProps={{ htmlInput: { style: { fontFamily: "ui-monospace, Menlo, monospace", fontSize: 13 } } }} />
            {err && <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>{err}</Typography>}
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                {p?.scalable
                    ? `${p.id} is scalable: each checked region gets ${base} base shard${base === 1 ? "" : "s"} (they never drain); the scaler adds and removes extra shards from the seat forecast (Scaling page).`
                    : "Each checked region gets one shard (this profile is not scalable: no extra shards are added)."}
                {" "}A shard's server is placed at once on a machine of its region with room (newest patch of that major.minor) and the shard opens
                when its server is ready; the server reads the data with <code>sessions get</code>. Players reach it through the title's own code.
            </Typography>
        </FormDialog>;
    }

    renderGroup(g) {
        const { netRegions } = this.state;
        const edit = canEdit();
        const columns = [
            { id: "region", label: "Region", width: 150, render: w => <Tooltip title={regionLabel(w.world.region, netRegions)}><Box sx={ELLIPSIS}>{regionLabel(w.world.region, netRegions)}</Box></Tooltip> },
            { id: "shard", label: "Shard", width: 95, render: w => <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                <strong>{`${String(w.world.region ?? "").toUpperCase()}-${w.world.shard ?? 1}`}</strong>
                {(w.world.base ?? w.world.shard == null) && <Tooltip title="Base shard: one of the region's minShardsPerRegion, never drained by the scaler"><Chip size="small" variant="outlined" label="base" /></Tooltip>}
              </Box> },
            { id: "state", label: "State", width: 95, render: w => <StateChip kind="session" state={w.world.state} title={w.world.reason ?? `since ${when(w.world.stateChangedAt)}`} /> },
            { id: "players", label: "Seated / joined / max", width: 110, align: "right", render: w => seats(w.world) },
            { id: "server", label: "Server", width: 185, render: w => w.server
                ? <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                    <StateChip kind="server" state={w.server.state} title={`${versionLabel(w.server.branch, w.server.versionText)} · desired ${w.server.desired}`} />
                    <IdText id={w.server.id} onClick={() => openMachine(w.server.machineId, w.server.id)} />
                  </Box>
                : <Typography variant="body2" color="text.secondary">none placed yet</Typography> },
            { id: "drain", label: "Scale-down", width: 105, render: w => w.autoDrain?.phase ? <AutoDrainChip autoDrain={w.autoDrain} /> : "—" },
            { id: "version", label: "Version", width: 95, render: w => sessionVersion(w.world) },
            { id: "successor", label: "Successor", width: 105, render: w => <IdText id={w.successor} /> },
            { id: "notice", label: "Stop scheduled", width: 110, render: w => w.server?.noticeAt > 0
                ? <Tooltip title={when(fromUnix(w.server.noticeAt))}><span>{fromUnix(w.server.noticeAt).toLocaleString([], { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span></Tooltip> : "—" },
            { id: "session", label: "Session", width: 110, render: w => <IdText id={w.world.id} onClick={() => openSession(w.world.id)} /> },
        ];
        return <Paper key={g.name} sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
                <Typography variant="h6" sx={{ mr: 1 }}>{g.name}</Typography>
                <Chip size="small" variant="outlined" label={g.first.profile} />
                <Chip size="small" variant="outlined" label={sessionVersion(g.first)} />
                {g.regions.map(([r, count]) => <Tooltip key={r} title={regionLabel(r, netRegions)}><Chip size="small" color="primary" variant="outlined" label={`${r} ×${count}`} /></Tooltip>)}
                <Typography variant="body2" color="text.secondary">{n(g.shards.length)} shard{g.shards.length === 1 ? "" : "s"} · {n(g.players)} joined · {n(g.seated)} seated</Typography>
                <Box sx={{ flex: 1 }} />
                {edit && <Tooltip title="Remove the world: every shard ends and its server stops">
                    <IconButton size="small" color="error" onClick={() => this.remove(g)}><DeleteIcon fontSize="small" /></IconButton>
                </Tooltip>}
            </Box>
            <DataTable columns={columns} rows={g.shards} getRowKey={w => w.world.id} sx={FIXED} />
        </Paper>;
    }

    render() {
        const { worlds, loadedAt, stale } = this.state;
        const groups = groupWorlds(worlds);
        return (
            <PageContainer maxWidth={1300}>
                <SectionHeader title="Worlds">
                    <StaleHint loadedAt={loadedAt} stale={stale} />
                    {canEdit() && <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={this.openAdd}>Add world</Button>}
                    <Tooltip title="Refresh"><IconButton size="small" onClick={this.loader.load}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
                </SectionHeader>
                {worlds == null
                    ? <Typography variant="body2" color="text.secondary">{stale ? `Could not load the worlds: ${stale}` : "Loading…"}</Typography>
                    : groups.length === 0
                        ? <Typography variant="body2" color="text.secondary">No live world.</Typography>
                        : groups.map(g => this.renderGroup(g))}
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                    A world is a group of shards: persistent sessions, each with its own server, per network region (BR-1, BR-2, US-1…). Base
                    shards never drain; extra shards are added by the scaler from the seat forecast and drained through the scale-down phases
                    (soft: no new rooms there; hard: players hop at a town; force: the rest hop after a notice). A shard never rolls to a new
                    patch by itself. Maintenance: schedule a stop on its server (Machines page) — at that time a successor on the newest patch
                    is placed and the shard re-opens there.
                </Typography>
                {this.renderDialog()}
            </PageContainer>
        );
    }
}
